import React, {
  useCallback, useContext, useEffect, useRef, useState, useMemo
} from 'react'
import PropTypes from 'prop-types'
import { usePromise } from 'react-use';
import { IframeClientContext } from '@yoti-web-share/iframe-client'
import { logger } from './logger'
import {
  getSharingSessionQrCodeAppLinkUrl,
  getSharingSessionQrCodeUrl,
  getSharingSessionRedirectUrl,
  getSharingSessionUrl,
} from './urls'
import request from './request'
import * as Commands from './commands'
import * as Resources from './resources'
import SharingStatuses from './SharingStatuses'
import ShareFlowContext from './ShareFlowContext'
import SharingSessionDescriptor from './SharingSessionDescriptor'
import useSharingSessionConnection from './useSharingSessionConnection'
import useReturningObserver from './useReturningObserver'
import FlowError from './errors/FlowError'

// TODO: make common
const MOBILE = 'mobile'
const MAX_RESUMING_TO_COMPLETE_DURATION = 8 * 1000

const finalSharingStatuses = [
  SharingStatuses.COMPLETED,
  SharingStatuses.TIMED_OUT,
  SharingStatuses.FAILED,
  SharingStatuses.REJECTED
]

const getLog = method => (name, ...elements) => {
  method.apply(logger, [`ShareFlowProvider - ${name}:`, ...elements])
}
const debug = getLog(logger.debug)
const warn = getLog(logger.warn)

const ShareFlowProvider = ({ children }) => {
  const safeAsync = usePromise()
  const [initialising, setInitialising] = useState(false)
  const [active, setActive] = useState(false)
  const [sharingSession, setSharingSession] = useState(null)
  const [error, setError] = useState(null)
  const [resuming, setResuming] = useState(false)

  const {
    clientConfiguration = {},
    hostContext = {},
    dataNotifier,
    actions
  } = useContext(IframeClientContext)

  const { device: { type: deviceType } = {} } = hostContext
  const {
    alwaysDisplayQrCode = false,
    useResumeMobileFlow = false
  } = clientConfiguration

  // Used to know if the connection should be established (only in QR code mode)
  // Additionally, this computation is then made common (Inline/Modal code)
  const connectOnceActive = useMemo(() => deviceType !== MOBILE || alwaysDisplayQrCode, [deviceType, alwaysDisplayQrCode])

  const {
    getResource: getResourceAction,
    commandTo: commandToAction,
  } = actions

  const {
    connect,
    disconnect,
    subscribe,
    connected,
    sharingStatus,
    profileToken,
    error: wsConnectionError,
    reset: resetConnection,
  } = useSharingSessionConnection(sharingSession, actions, dataNotifier)

  const {
    observe: observeReturn,
    unobserve: unobserveReturn,
    hasReturned
  } = useReturningObserver()


  const activeRef = useRef(false)
  const connectedRef = useRef(false)
  const wsConnectionErrorRef = useRef(wsConnectionError)
  const errorRef = useRef(error)

  const requestShareUrl = useCallback(
    () => getResourceAction(Resources.SHARE_URL),
    [getResourceAction]
  )

  const completeShare = useCallback(
    (token, url) => commandToAction(Commands.COMPLETE_SHARE, { token, url }),
    [commandToAction]
  )

  const openAppLink = useCallback(
    (url, targetAppFirst) => commandToAction(Commands.OPEN_MOBILE_APP_LINK, { url, targetAppFirst }),
    [commandToAction]
  )

  const completeShareFlow = useCallback(
    async () => {
      try {
        const redirectUrl = getSharingSessionRedirectUrl(sharingSession, hostContext, { token: profileToken })
        await completeShare(profileToken, redirectUrl)
      } catch (err) {
        warn('failed to send share complete', err)
        // TODO: in case the share complete fails...
      }
    },
    [profileToken, sharingSession, hostContext, completeShare]
  )

  const initialiseShareFlow = useCallback(async () => {
    debug('initialiseShareFlow !!!')
    if (initialising) {
      warn('initialiseShareFlow', 'share already initialising, should not be called')
      return
    }

    if (active) {
      warn('initialiseShareFlow', 'share is active, should not be called until it is no longer active')
      return
    }

    resetConnection()
    setInitialising(true)
    setError(undefined)
    setResuming(false)
    activeRef.current = false
    connectedRef.current = false
    wsConnectionErrorRef.current = undefined
    errorRef.current = undefined

    debug('initialiseShareFlow', 'reset done')

    try {
      const { hasShareUrlProvider } = clientConfiguration
      let shareUrl
      if (hasShareUrlProvider) {
        shareUrl = await requestShareUrl()
      }
      const sharingSessionUrl = getSharingSessionUrl(clientConfiguration, shareUrl)
      const sharingSessionData = await safeAsync(request(sharingSessionUrl))
      const sharingSessionDescriptor = new SharingSessionDescriptor(sharingSessionData)
      setSharingSession(sharingSessionDescriptor)

      setActive(true)
      setInitialising(false)
    } catch (e) {
      warn('initialiseShareFlow', e)
      setInitialising(false)
      setError(e)
    }
  }, [
    safeAsync,
    active,
    initialising,
    requestShareUrl,
    clientConfiguration,
    resetConnection
  ])

  const resumeShareFlow = useCallback(() => {
    setResuming(true)
    connect()
  }, [connect])

  const standByUntilUserReturnToResumeShareFlow = useCallback(() => {
    if (useResumeMobileFlow) {
      observeReturn()
    }
  }, [observeReturn, useResumeMobileFlow])

  useEffect(() => {
    if (connectOnceActive && activeRef.current !== active && active) {
      connect()
    }
    activeRef.current = active
  }, [active, connect, connectOnceActive])

  useEffect(() => {
    if (connectedRef.current !== connected) {
      if (connected) {
        subscribe(resuming)
      }
    }
    connectedRef.current = connected
  }, [connected, subscribe, resuming])

  useEffect(() => {
    debug('useEffect[sharingStatus]', { sharingStatus })
    if (sharingStatus === SharingStatuses.RESUMED) {
      setResuming(false)
    }
    if (finalSharingStatuses.includes(sharingStatus)) {
      setActive(false)
    }
  }, [sharingStatus])

  // Specific to the resume flow, when receiving RESUMED we expect the token shortly after
  useEffect(() => {
    debug('useEffect[sharingStatus] before setup time out', { sharingStatus })
    if (sharingStatus === SharingStatuses.RESUMED) {
      debug('useEffect[sharingStatus] setup time out')
      const neverCompleteAfterResumedTimeout = setTimeout(() => {
        setError(new FlowError(FlowError.Reasons.COMPLETE_TIMED_OUT))
      }, MAX_RESUMING_TO_COMPLETE_DURATION)
      return () => { clearTimeout(neverCompleteAfterResumedTimeout) }
    }
    return () => { }
  }, [sharingStatus])

  useEffect(() => {
    debug('useEffect[profileToken, completeShareFlow]', { profileToken, completeShareFlow })
    if (profileToken) {
      completeShareFlow()
        .catch((err) => {
          warn('useEffect[profileToken]', 'completeShareFlow() failed', err)
        })
    }
  }, [profileToken, completeShareFlow])

  useEffect(() => {
    debug('useEffect[hasReturned, unobserveReturn, resumeShareFlow]', { hasReturned, unobserveReturn, resumeShareFlow })
    if (hasReturned) {
      unobserveReturn()
      resumeShareFlow()
    }
  }, [hasReturned, unobserveReturn, resumeShareFlow])

  useEffect(() => {
    debug('useEffect[wsConnectionError, active]', { wsConnectionError, active })
    const neverHadConnectionError = wsConnectionErrorRef.current === undefined
    if (wsConnectionError && neverHadConnectionError && active) {
      wsConnectionErrorRef.current = wsConnectionError
      debug('caught connection error', wsConnectionError)
      setError(wsConnectionError)
    }
  }, [wsConnectionError, active])

  useEffect(() => {
    debug('useEffect[error, disconnect]', { error, disconnect })
    const neverHadError = errorRef.current === undefined
    if (error && neverHadError) {
      errorRef.current = error
      warn(error)
      setInitialising(false)
      setResuming(false)
      setActive(false)
      disconnect()
    }
  }, [error, disconnect])

  let qrCodeUrl
  let qrCodeAppLinkUrl
  let details
  if (sharingSession && !initialising) {
    qrCodeUrl = getSharingSessionQrCodeUrl(sharingSession, clientConfiguration)
    qrCodeAppLinkUrl = getSharingSessionQrCodeAppLinkUrl(sharingSession, clientConfiguration, hostContext)
    details = {
      organisation: sharingSession.getOrganisation(),
      application: sharingSession.getApplication(),
      attributes: sharingSession.getAttributes(),
      identityProfile: sharingSession.getIdentityProfile(),
      advancedIdentityProfile: sharingSession.getAdvancedIdentityProfile(),
    }
  }

  let refinedSharingStatus = sharingStatus
  refinedSharingStatus = initialising ? '' : refinedSharingStatus

  // In some rare case, the websocket is closed as soon as also receiving one of the final status
  // That can sometimes lead to receiving the error (wsConnectionError) while 'active' is still true
  // (although it should not because receiving any final status should lead to setting 'active' to false in the previous hooks cycle)
  const ignoreError = finalSharingStatuses.includes(sharingStatus)

  const context = {
    init: initialiseShareFlow,
    closeConnection: disconnect,
    standByUntilUserReturn: standByUntilUserReturnToResumeShareFlow,
    openAppLink,
    initialising,
    resuming,
    active,
    sharingStatus: refinedSharingStatus,
    qrCodeUrl,
    qrCodeAppLinkUrl,
    details,
    error: ignoreError ? undefined : error,
    displayAsQrCode: connectOnceActive
  }

  return (
    <ShareFlowContext.Provider value={context}>
      {children}
    </ShareFlowContext.Provider>
  )
}

ShareFlowProvider.defaultProps = {
  children: null,
}

ShareFlowProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.element
  ])
}

export default ShareFlowProvider
